/**
 * Pre-reading dossier content — per-workshop documents displayed by
 * `DocModal`. WS4 carries the fully-ported vanilla copy; other
 * workshops fall back to a same-shape placeholder so the modal still
 * reads as content (not as a stub).
 *
 * Ported from:
 *   - `_legacy/ws4_live_content.js` lines 143-186 — `WS4_PREREADING.data`
 *     (heading, subheading, 5 sections incl. the bordered "Key Insight"
 *     callout).
 *   - `_legacy/ws4-modals.js` lines 33-67 — `renderDocModal()` (the
 *     vanilla flattener that emitted heading/body/intro/items/callout
 *     into a single scrolling pane).
 *   - `_legacy/wd.html` line 113 — `#docModal` shell width (the prompt
 *     calls out `58vw; max-width:660px` per ~line 400; that exact spec
 *     belongs to the Bring modal — the Doc shell is `72vw; max-width:820px`
 *     and the React port keeps the wider Doc width to match siblings).
 *
 * The vanilla data model had `body` / `intro` / `items` / `callout`
 * variants. We model those as a discriminated `kind`:
 *   - `"section"` — prose section heading (sentence-case Inter 700
 *     fs-title, no uppercase chrome) followed by `body` paragraphs
 *     and/or a bullet `items` list.
 *   - `"callout"` — tinted left-bordered block. The heading retains
 *     the uppercase eyebrow look because callouts are visually distinct
 *     from prose flow.
 *
 * The previous shape (`body: string | string[]`) flattened the Key
 * Insight callout and the Objectives intro+items into a single
 * paragraph array; both are now restored to their legacy structure.
 */

import type { Workshop } from "@/content/workshops";

export type DocSection = {
  heading: string;
  /** Visual treatment. Defaults to `"section"` if omitted. */
  kind?: "section" | "callout";
  /** Lead paragraph(s). String or array of paragraphs. */
  body?: string | string[];
  /** Optional intro paragraph that precedes `items`. */
  intro?: string;
  /** Optional bullet list — emitted as `<ul>` after `body`/`intro`. */
  items?: string[];
};

export type DocContent = {
  title: string;
  sections: DocSection[];
};

type WorkshopId = Workshop["id"];

/* ----------------------------- WS4 -------------------------------- */

const WS4_DOC: DocContent = {
  title: "Pre-reading · Workshop 4",
  sections: [
    {
      heading: "Overview",
      body: "Workshop 4 is the fourth session in the XAES Phase Zero programme and marks a pivotal transition — from understanding AI capabilities in isolation to designing how humans and AI agents work together in live operations. This session moves beyond taxonomy and theory: participants will map real roles and responsibilities, calibrate operating modes against actual tasks, and stress-test their assumptions with structured scenario work.",
    },
    {
      heading: "Objectives",
      intro:
        "By the end of WS4, participants will have collectively produced three tangible outputs and reached shared agreement on:",
      items: [
        "A Role and Responsibility Map with explicit dual ownership fields (execution vs. accountability)",
        "A draft Operating Mode Assignment Matrix, domain-mapped and risk-calibrated",
        "A set of Handoff Protocol principles, stress-tested and ready for ratification",
        "A shared vocabulary for human-agent governance that travels into subsequent workshops",
      ],
    },
    {
      heading: "Background Context",
      body: "Across the first three XAES workshops, participants developed a shared understanding of the AI capability landscape, identified their highest-value use cases, and began mapping the human skills needed in a hybrid operating model. WS4 builds directly on those foundations. The four Human-Agent operating modes — In-the-Loop, Above-the-Loop, On-the-Loop, and Out-of-Loop — have been introduced in earlier sessions. Today we put them to work.",
    },
    {
      heading: "Key Insight",
      kind: "callout",
      body: "Human oversight is not a binary — it exists on a spectrum defined by task criticality, reversibility of agent actions, regulatory exposure, and organisational trust maturity. The goal of this workshop is to map your real tasks onto that spectrum with enough precision that the resulting Mode Assignment Matrix can guide deployment decisions.",
    },
    {
      heading: "What to Expect",
      body: "The session is structured around four activities with tight timebox constraints. Expect debate — disagreement about mode assignments is a feature, not a bug. The facilitator will guide groups through structured deliberation but will not resolve disputes by fiat. Decisions reached today are directional: they inform the charter and go to the leadership team for ratification. Come prepared to be honest about where your team's trust in AI is high, and where it is not.",
    },
  ],
};

/* --------------------------- Placeholders ------------------------- */

function placeholderDoc(num: number, longTitle: string): DocContent {
  return {
    title: `Pre-reading · Workshop ${num}`,
    sections: [
      {
        heading: "Overview",
        body: `${longTitle} — pre-reading content for this workshop is being prepared.`,
      },
      {
        heading: "Status",
        body: "The dossier will be circulated ahead of the session. In the meantime, refer to the questionnaire and the WS4 dossier for the operating-mode framework that anchors the programme.",
      },
    ],
  };
}

/* ------------------------------ Map ------------------------------- */

export const DOC_BY_WORKSHOP: Record<WorkshopId, DocContent> = {
  WS1: placeholderDoc(1, "WS1 – Executive Alignment"),
  WS2: placeholderDoc(2, "WS2 – Current Process Selection & Baseline"),
  WS3: placeholderDoc(3, "WS3 – To-Be AI Enhanced Process Design"),
  WS4: WS4_DOC,
  WS5: placeholderDoc(5, "WS5 – Governance & Guardrails"),
  WS6: placeholderDoc(6, "WS6 – Target Operating Model"),
  WS7: placeholderDoc(7, "WS7 – Path to Value"),
  WS8: placeholderDoc(8, "WS8 – Launch / Flight Plan"),
};
